import React, { useMemo } from "react";

const A4_WIDTH = 794;
const A4_HEIGHT = 1122;
const PAGE_LINES = 30; // Approximate lines per page

function stripHtml(html = "") {
  if (typeof window === "undefined") return html;
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
}

function ResumePreview({ resumeData = {}, template = "Template 1" }) {
  const defaultData = {
    name: "Your Name",
    email: "your.email@example.com",
    phone: "1234567890",
    position: "Job Title",
    summary: "Your summary details go here...",
    education: "Your education details go here...",
    experience: "Your experience details go here...",
    skills: "Skill details...",
    languages: "English, Hindi"
  };

  const sectionTitle = {
    fontSize: "18px",
    fontWeight: "bold",
    marginTop: "20px",
    paddingBottom: "5px",
    borderBottom: "1px solid"
  };

  const templates = {
    "Template 1": { container:{fontFamily:"Segoe UI",color:"#0e283e",background:"#fff",border:"1px solid #cce0f5"}, section:{...sectionTitle,borderBottomColor:"#007bff"}, sidebar:{width:"30%",background:"#2CBCAD",padding:"30px",color:"#fff"} },
    "Template 2": { container:{fontFamily:"Times New Roman",color:"#333",background:"#fff",border:"1px solid #dcdcdc"}, section:{...sectionTitle,borderBottomColor:"#999"}, sidebar:{width:"30%",background:"#f5f5f5",padding:"30px"} },
    "Template 3": { container:{fontFamily:"Comic Sans MS",color:"#aa336a",background:"#fff",border:"1px solid #ff80ab"}, section:{...sectionTitle,borderBottomColor:"#ff80ab"}, sidebar:{width:"30%",background:"#D8A968",padding:"30px",color:"#fff"} },
    "Template 4": { container:{fontFamily:"Helvetica",color:"#222",background:"#fff",border:"1px solid #eee"}, section:{...sectionTitle,borderBottomColor:"#ccc"}, sidebar:{width:"30%",background:"#3C66B0",padding:"30px",color:"#fff"} },
    "Template 5": { container:{fontFamily:"Georgia",color:"#000",background:"#fff",border:"1px solid #bbb"}, section:{...sectionTitle,borderBottomColor:"#444"}, sidebar:{width:"30%",background:"#38455C",padding:"30px",color:"#fff"} },
    "Template 6": { container:{fontFamily:"Courier New",color:"#333",background:"#fff",border:"1px solid #c2e8d2"}, section:{...sectionTitle,borderBottomColor:"#6abf69"}, sidebar:{width:"30%",background:"#618553",padding:"30px",color:"#fff"} },
    "Template 7": { container:{fontFamily:"Roboto Slab",color:"#222",background:"#fcfcfc",border:"1px solid #e0e0e0"}, section:{...sectionTitle,borderBottomColor:"#005f73",color:"#005f73"}, sidebar:{width:"30%",background:"#C34C8C",padding:"30px",color:"#fff"} },
    "Template 8": { container:{fontFamily:"Lato",color:"#1a1a1a",background:"#fff",border:"1px solid #ddd"}, section:{...sectionTitle,borderBottomColor:"#333"}, sidebar:{width:"30%",background:"#E4A829",padding:"30px",color:"#fff"} },
    "Template 9": { container:{fontFamily:"PT Sans",color:"#2e2e2e",background:"#fdfdfd",border:"1px solid #d1d1d1"}, section:{...sectionTitle,borderBottomColor:"#4a4a4a"}, sidebar:{width:"30%",background:"#8DD3D3",padding:"30px",color:"#333"} },
    "Template 10": { container:{fontFamily:"Montserrat",color:"#333",background:"#fefefe",border:"1px solid #ddd"}, section:{...sectionTitle,borderBottomColor:"#6c63ff",color:"#6c63ff"}, sidebar:{width:"30%",background:"#543477",padding:"30px",color:"#fff"} }
  };

  const styles = templates[template] || templates["Template 1"];

  // Split sections into lines
  const allLines = useMemo(() => {
    const data = [];
    const sections = [
      { title: "Summary", content: resumeData.summary || defaultData.summary },
      { title: "Education", content: resumeData.education || defaultData.education },
      { title: "Experience", content: resumeData.experience || defaultData.experience },

    ];
    sections.forEach(sec => {
      const lines = stripHtml(sec.content).split("\n");
      data.push({ title: sec.title, lines });
    });
    return data;
  }, [resumeData]);

  // Paginate based on line count
  const pages = useMemo(() => {
    const result = [];
    let currentPage = [];
    let currentLineCount = 0;

    allLines.forEach(section => {
      section.lines.forEach((line, index) => {
        if (index === 0) currentPage.push({ type: "title", text: section.title });
        currentPage.push({ type: "line", text: line });
        currentLineCount++;

        if (currentLineCount >= PAGE_LINES) {
          result.push(currentPage);
          currentPage = [];
          currentLineCount = 0;
        }
      });
    });

    if (currentPage.length) result.push(currentPage);
    return result;
  }, [allLines]);

  return (
    <div>
      {pages.map((page, pageIndex) => (
        <div
          key={pageIndex}
          id={`resume-page-${pageIndex}`} 
          style={{
            ...styles.container,
            width: A4_WIDTH,
            height: A4_HEIGHT,
            margin: "30px auto",
            borderRadius: "10px",
            display: "flex",
            overflow: "hidden",
            boxShadow: "0 0 10px rgba(0,0,0,0.1)"
          }}
        >
          {/* Sidebar always 30% width, content only on first page */}
          <div style={{ ...styles.sidebar, height: "100%", boxSizing: "border-box" }}>
            {pageIndex === 0 && (
              <>
                <p style={{ margin: "5px 0", wordBreak: "break-word" }}>
                  <b>Email:</b> {resumeData.email || defaultData.email}
                </p>
                <p style={{ margin: "5px 0", wordBreak: "break-word" }}>
                  <b>Phone:</b> {resumeData.phone || defaultData.phone}
                </p>
                <p style={{ margin: "5px 0", wordBreak: "break-word" }}>
                  <b>Languages:</b> {resumeData.languages || defaultData.languages}
                </p>
                <p style={{ margin: "5px 0", wordBreak: "break-word" }}>
                  <b>Skills:</b> {resumeData.skills || defaultData.skills}
                </p>
              </>
            )}
          </div>

          {/* Main content always 70% width */}
          <div style={{ width: "70%", padding: "30px", boxSizing: "border-box" }}>
            {pageIndex === 0 && (
              <>
                <h2>{resumeData.name || defaultData.name}</h2>
                <h4 style={{ color: "#777", marginBottom: "20px" }}>
                  {resumeData.position || defaultData.position}
                </h4>
              </>
            )}

            {page.map((item, i) => {
              if (item.type === "title") return <div key={i} style={styles.section}>{item.text}</div>;
              return <p key={i} style={{ whiteSpace: "pre-wrap", lineHeight: "1.6", margin: 0 }}>{item.text}</p>;
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export default ResumePreview;